import { useEffect, useState } from "react"

import type { AuditLogEntry } from "../../../types/audit-log.types.ts"
import { getLeadDetails, getLeadLogHistory } from "../../Lead/api/services.ts"
import { getProjectDocuments } from "../../Project/api/project-documents.services.ts"
import { getProjectLogHistory } from "../../Project/api/project.services.ts"
import { visibleLeadAttachments } from "../../Lead/LeadWorkspacePage/lead-attachments.ts"
import type { EntityType } from "../redux/global-map.types"

interface QuickViewCounts {
  files: number | null
  activity: number | null
}

/**
 * The real Files/Activity totals for the panel's tab labels.
 *
 * The approved design shows a count on each tab, so they have to be known
 * before the tab is opened — but mounting the Files tab itself just to learn
 * its count would run its whole folders/albums/documents batch on every
 * marker click. This asks only for the counts: one page-of-one documents
 * request (DRF answers the real `count` whatever the page size) and the same
 * log-history request the Activity tab reads.
 *
 * Both start `null`, which is what keeps a count out of the label until the
 * answer is real — never a zero the data has not confirmed. A failed request
 * stays `null` for the same reason. Files permissions are the endpoints' own:
 * nothing here widens what the caller may see.
 */
export function useQuickViewCounts(
  entityId: string | null,
  entityType: EntityType | null
): QuickViewCounts {
  const [counts, setCounts] = useState<QuickViewCounts>({
    files: null,
    activity: null
  })

  useEffect(() => {
    let cancelled = false
    setCounts({ files: null, activity: null })
    if (!entityId || !entityType) return

    // Each count is best-effort: a label is the only thing riding on it, so
    // a refusal, a network failure — or a caller that stubs these services
    // out — must leave the count unknown, never take the panel down with it.
    const settle = <T>(value: Promise<T> | undefined): Promise<T | null> =>
      Promise.resolve(value ?? null).catch(() => null) as Promise<T | null>

    const filesRequest =
      entityType === "project"
        ? settle(
            getProjectDocuments({ lead: entityId, page: 1, page_size: 1 })
          ).then((response) =>
            response?.ok && response.data ? (response.data.count ?? null) : null
          )
        : settle(getLeadDetails(entityId)).then((response) =>
            response?.ok && response.data
              ? visibleLeadAttachments(response.data.attachments).length
              : null
          )

    const logHistory: Promise<{ ok: boolean; data?: unknown }> | undefined =
      entityType === "lead"
        ? getLeadLogHistory(entityId)
        : getProjectLogHistory(entityId)

    const activityRequest = settle(logHistory).then((response) =>
      response?.ok && Array.isArray(response.data)
        ? (response.data as AuditLogEntry[]).length
        : null
    )

    void Promise.all([filesRequest, activityRequest]).then(
      ([files, activity]) => {
        if (cancelled) return
        setCounts({ files, activity })
      }
    )

    return () => {
      cancelled = true
    }
  }, [entityId, entityType])

  return counts
}

export default useQuickViewCounts
